import React, { useState, useEffect } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Button, Typography, Tooltip } from '@mui/material';
import { StoneXDatePicker, StoneXTextField, StoneXNumber } from '../../../StoneXMui';
import AddIcon from '@mui/icons-material/Add';
import NotesIcon from '@mui/icons-material/Notes';
import dayjs from 'dayjs';
import { BudgetRoundModel, Unit, UpdateBudgetRoundRequest, UpdateBudgetsRequest, UpdateBudgetModel, BudgetModel } from '../../../../Generated/Commodity-Management-Api';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import { useSnackbar } from 'notistack';
import { convertVolume } from '../../../../Helpers';

interface ForecastRow {
    budgetMonthLabel: string;
    quantity?: number | null;
    unit?: Unit | null;
    forecasts: Record<number, { quantity?: number; note?: string; }>; // key: budgetRoundId
}

export interface ForecastData {
    rows: ForecastRow[];
    forecastRounds: BudgetRoundModel[];
}

interface ForecastTableProps {
    forecastData: ForecastData;
    onForecastSave: () => void;
    budgets: BudgetModel[]; // These are the displayed budgets (possibly converted)
    originalBudgets: BudgetModel[]; // Original budgets with original units
    selectedLocationId: number;
    selectedCommodityId: number;
    displayUnit?: Unit; // The current display unit
}

const ForecastTable: React.FC<ForecastTableProps> = ({
    forecastData,
    onForecastSave,
    budgets,
    originalBudgets,
    selectedLocationId,
    selectedCommodityId,
    displayUnit,
}) => {
    const [isAddingForecast, setIsAddingForecast] = useState(false);
    const [newForecastName, setNewForecastName] = useState('');
    const [newForecastQuantities, setNewForecastQuantities] = useState<Record<string, number | string>>({});
    const [newForecastNotes, setNewForecastNotes] = useState<Record<string, string>>({});
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (isAddingForecast) {
            const initialQuantities = forecastData.rows.reduce((acc, row) => {
                const mostRecentForecastRoundId = forecastData.forecastRounds[0]?.budgetRoundId;
                const forecastQuantity = mostRecentForecastRoundId ? row.forecasts[mostRecentForecastRoundId]?.quantity : undefined;
                acc[row.budgetMonthLabel] = forecastQuantity ?? row.quantity ?? '';
                return acc;
            }, {} as Record<string, number | string>);
            setNewForecastQuantities(initialQuantities);
        } else {
            setNewForecastName('');
            setNewForecastQuantities({});
            setNewForecastNotes({});
        }
    }, [isAddingForecast, forecastData.rows, forecastData.forecastRounds]);

    const handleNewForecastQuantityChange = (month: string, value: string) => {
        const numericValue = value === '' ? '' : Number(value);
        if (numericValue === '' || !isNaN(Number(numericValue)) && Number(numericValue) >= 0) {
            setNewForecastQuantities(prev => ({ ...prev, [month]: numericValue }));
        }
    };

    const handleNewForecastNoteChange = (month: string, value: string) => {
        setNewForecastNotes(prev => ({ ...prev, [month]: value }));
    };

    const handleSaveForecast = async () => {
        if (!newForecastName.trim()) {
            enqueueSnackbar('Please enter a forecast name.', { variant: 'warning' });
            return;
        }

        const hasMissingQuantities = forecastData.rows.some(row => {
            const quantity = newForecastQuantities[row.budgetMonthLabel];
            return quantity === '' || quantity === undefined || quantity === null;
        });

        if (hasMissingQuantities) {
            enqueueSnackbar('Please enter a quantity for all months.', { variant: 'warning' });
            return;
        }

        try {
            const budgetRoundRequest: UpdateBudgetRoundRequest = {
                budgetRoundId: 0,
                budgetRoundName: newForecastName,
                isDefault: true,
                entryDate: dayjs().format(),
            };
            const budgetRoundResponse = await CommodityManagementApi.updateBudgetRound(null, budgetRoundRequest);
            const newBudgetRoundId = budgetRoundResponse.data.budgetRound?.budgetRoundId;

            if (!newBudgetRoundId) {
                throw new Error("Failed to create budget round.");
            }

            // Use original budgets to get the original unit for conversion
            const baseBudgets = originalBudgets.filter(b =>
                b.budgetRoundModel.budgetRoundName === "Base" &&
                b.location.locationId === selectedLocationId &&
                b.commodity.commodityId === selectedCommodityId
            );

            const newBudgets: UpdateBudgetModel[] = baseBudgets.map(baseBudget => {
                const monthLabel = dayjs(`${baseBudget.budgetDate.year}-${baseBudget.budgetDate.month}-01`).format('MMM-YY');
                const displayQuantity = Number(newForecastQuantities[monthLabel]);

                // Convert from display unit back to original budget unit if different
                let volumeToSave = displayQuantity;
                if (displayUnit && baseBudget.unit && displayUnit !== baseBudget.unit) {
                    const bushelVolume = baseBudget.budgetPricingStrategy?.bushelVolume;
                    volumeToSave = convertVolume(
                        displayQuantity,
                        displayUnit,
                        baseBudget.unit,
                        bushelVolume ?? undefined
                    );
                }

                return {
                    budgetId: 0,
                    budgetRoundId: newBudgetRoundId,
                    volume: volumeToSave,
                    budgetDate: baseBudget.budgetDate,
                    commodityId: baseBudget.commodity.commodityId,
                    locationId: baseBudget.location.locationId,
                    pricingStrategyId: baseBudget.budgetPricingStrategy.budgetPricingStrategyId,
                    unit: baseBudget.unit, // Save with the original unit
                    budgetPrices: baseBudget.budgetPrices,
                    currency: baseBudget.currency,
                    contractDate: baseBudget.contractDate,
                    note: newForecastNotes[monthLabel] ?? '',
                };
            });

            const updateRequest: UpdateBudgetsRequest = {
                budgets: newBudgets,
            };

            await CommodityManagementApi.updateBudgets(null, updateRequest);

            enqueueSnackbar('New forecast saved successfully!', { variant: 'success' });
            setIsAddingForecast(false);
            onForecastSave();
        } catch (error) {
            console.error("Failed to save new forecast", error);
            enqueueSnackbar('An error occurred while saving the new forecast.', { variant: 'error' });
        }
    };

    return (
        <>
            <TableContainer component={Paper} sx={{ width: 'auto' }}>
                <Table size="small" sx={{ width: 'auto' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Budget Month</TableCell>
                            <TableCell>Base Volume</TableCell>
                            <TableCell>Unit</TableCell>
                            {forecastData.forecastRounds.map(round => (
                                <TableCell key={round.budgetRoundId}>
                                    Forecast
                                    <br />
                                    {round.budgetRoundName}
                                </TableCell>
                            ))}
                            <TableCell>
                                {isAddingForecast ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <StoneXDatePicker onChange={(e) => setNewForecastName(e ? dayjs(e).format("MM/DD/YYYY") : '')} label="New Forecast" width='small' />
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Typography variant="caption" sx={{ width: '120px', textAlign: 'center' }}>Quantity</Typography>
                                            <Typography variant="caption" sx={{ width: '120px', textAlign: 'center' }}>Notes</Typography>
                                        </Box>
                                    </Box>
                                ) : (
                                    <IconButton onClick={() => setIsAddingForecast(true)} size="small">
                                        <AddIcon />
                                    </IconButton>
                                )}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {forecastData.rows.map(row => (
                            <TableRow key={row.budgetMonthLabel}>
                                <TableCell>{row.budgetMonthLabel}</TableCell>
                                <TableCell>{row.quantity?.toLocaleString()}</TableCell>
                                <TableCell>{displayUnit ?? row.unit}</TableCell>
                                {forecastData.forecastRounds.map(round => {
                                    const forecast = row.forecasts[round.budgetRoundId];
                                    return (
                                        <TableCell key={round.budgetRoundId}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span>{forecast?.quantity?.toLocaleString()}</span>
                                                {forecast?.note && (
                                                    <Tooltip title={forecast.note}>
                                                        <NotesIcon sx={{ fontSize: '1rem', color: 'text.secondary', ml: 1 }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    );
                                })}
                                <TableCell>
                                    {isAddingForecast && (
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <StoneXTextField
                                                type="number"
                                                width="120px"
                                                value={newForecastQuantities[row.budgetMonthLabel] ?? ''}
                                                onChange={(e) => handleNewForecastQuantityChange(row.budgetMonthLabel, e.target.value)}
                                            />
                                            <StoneXTextField
                                                width="120px"
                                                value={newForecastNotes[row.budgetMonthLabel] ?? ''}
                                                onChange={(e) => handleNewForecastNoteChange(row.budgetMonthLabel, e.target.value)}
                                            />
                                        </Box>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}

                        {forecastData.rows.length > 0 && (
                            <TableRow>
                                <TableCell align="right"><strong>Total:</strong></TableCell>
                                <TableCell>{forecastData.rows.reduce((sum, row) => sum + (row.quantity || 0), 0).toLocaleString()}</TableCell>
                                <TableCell>{/* Unit Total */}</TableCell>
                                {forecastData.forecastRounds.map(round => (
                                    <TableCell key={`total-${round.budgetRoundId}`}>
                                        {forecastData.rows.reduce((sum, row) => sum + (row.forecasts[round.budgetRoundId]?.quantity || 0), 0).toLocaleString()}
                                    </TableCell>
                                ))}
                                <TableCell>
                                    {isAddingForecast && (
                                        <Box sx={{ display: 'flex', gap: 1, width: '248px', justifyContent: 'flex-start' }}>
                                            <Box sx={{ width: '120px' }}>
                                                <StoneXNumber
                                                    number={Object.values(newForecastQuantities)
                                                        .map(val => typeof val === 'number' ? val : Number(val) || 0)
                                                        .reduce((sum, val) => sum + val, 0)}
                                                />
                                            </Box>
                                        </Box>
                                    )}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {isAddingForecast && (
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button variant="contained" onClick={handleSaveForecast}>Save</Button>
                    <Button variant="outlined" onClick={() => setIsAddingForecast(false)}>Cancel</Button>
                </Box>
            )}
        </>
    );
};

export default ForecastTable;
